/**
 * Toast, sheet and confetti â€” the three bits of chrome the screens share.
 */

import { append, clear, h, svg } from './dom';
import { reducedMotion } from './motion';

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

let toastHost: HTMLElement | null = null;

function host(): HTMLElement {
  if (!toastHost) {
    toastHost = h('div', { class: 'toasts', attrs: { role: 'status', 'aria-live': 'polite' } });
    document.getElementById('app')?.appendChild(toastHost);
  }
  return toastHost;
}

export function toast(message: string, ms = 1900): void {
  const node = h('div', { class: 'toast', text: message });
  host().appendChild(node);
  setTimeout(() => {
    node.classList.add('toast--out');
    setTimeout(() => node.remove(), 300);
  }, ms);
}

// ---------------------------------------------------------------------------
// Sheet
// ---------------------------------------------------------------------------

export interface SheetOptions {
  title?: string;
  /** Focused when the sheet opens, for keyboard and screen reader users. */
  autofocus?: HTMLElement;
}

let openSheet: { node: HTMLElement; close: () => void } | null = null;

export function sheet(build: (close: () => void) => HTMLElement[], options: SheetOptions = {}): () => void {
  if (openSheet) openSheet.close();

  const body = h('div', { class: 'sheet__body', attrs: { role: 'dialog', 'aria-modal': 'true' } });
  if (options.title) body.setAttribute('aria-label', options.title);

  const scrim = h('div', { class: 'sheet__scrim' });
  const root = h('div', { class: 'sheet', attrs: { 'aria-hidden': 'false' } }, scrim, body);

  const previouslyFocused = document.activeElement as HTMLElement | null;

  const close = () => {
    if (openSheet?.node !== root) return;
    openSheet = null;
    root.classList.remove('sheet--in');
    document.removeEventListener('keydown', onKey);
    setTimeout(() => root.remove(), 420);
    previouslyFocused?.focus?.();
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  scrim.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  body.appendChild(h('div', { class: 'sheet__grip' }));
  append(body, build(close));

  document.getElementById('app')?.appendChild(root);
  openSheet = { node: root, close };

  requestAnimationFrame(() => root.classList.add('sheet--in'));
  setTimeout(() => (options.autofocus ?? body).focus?.(), 260);

  return close;
}

export function closeSheet(): void {
  openSheet?.close();
}

export function isSheetOpen(): boolean {
  return openSheet !== null;
}

/** A sheet with a heading, a close button and a body â€” the common shape. */
export function panelSheet(
  title: string,
  build: (close: () => void) => HTMLElement[],
): () => void {
  return sheet((close) => [
    h(
      'div',
      { class: 'section__head' },
      h('div', { class: 'bar__title', text: title }),
      h('button', {
        class: 'icon',
        attrs: { type: 'button', 'aria-label': 'Close' },
        on: { click: close },
      }, svg('M6 6l12 12M18 6L6 18')),
    ),
    ...build(close),
  ], { title });
}

// ---------------------------------------------------------------------------
// Confetti
// ---------------------------------------------------------------------------

interface Bit {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  vr: number;
  colour: string;
  life: number;
}

let canvas: HTMLCanvasElement | null = null;
let bits: Bit[] = [];
let raf = 0;

function surface(): HTMLCanvasElement | null {
  if (canvas) return canvas;
  canvas = document.getElementById('confetti') as HTMLCanvasElement | null;
  return canvas;
}

function resize(): void {
  const c = surface();
  if (!c) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  c.width = c.clientWidth * dpr;
  c.height = c.clientHeight * dpr;
  c.getContext('2d')?.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function loop(): void {
  const c = surface();
  const g = c?.getContext('2d');
  if (!c || !g) return;

  g.clearRect(0, 0, c.clientWidth, c.clientHeight);

  for (const b of bits) {
    b.vy += 0.34;
    b.vx *= 0.992;
    b.x += b.vx;
    b.y += b.vy;
    b.rot += b.vr;
    b.life -= 1;

    g.save();
    g.translate(b.x, b.y);
    g.rotate(b.rot);
    g.globalAlpha = Math.max(0, Math.min(1, b.life / 40));
    g.fillStyle = b.colour;
    g.fillRect(-b.w / 2, -b.h / 2, b.w, b.h);
    g.restore();
  }

  bits = bits.filter((b) => b.life > 0 && b.y < c.clientHeight + 60);

  if (bits.length) raf = requestAnimationFrame(loop);
  else {
    g.clearRect(0, 0, c.clientWidth, c.clientHeight);
    raf = 0;
  }
}

export interface BurstOptions {
  colours: string[];
  /** 0..1 â€” how much of the screen to fill. */
  power?: number;
  originY?: number;
}

export function burst({ colours, power = 0.5, originY = 0.34 }: BurstOptions): void {
  const c = surface();
  if (!c || reducedMotion()) return;

  resize();
  const count = Math.round(70 + power * 190);
  const w = c.clientWidth;

  for (let i = 0; i < count; i++) {
    // Two side cannons plus a centre puff: reads as celebration, not snowfall.
    const fromLeft = i % 3 === 0;
    const fromRight = i % 3 === 1;
    const speed = (6 + Math.random() * 11) * (0.6 + power * 0.8);
    const angle = fromLeft
      ? -Math.PI / 3 + Math.random() * 0.7
      : fromRight
        ? (-Math.PI * 2) / 3 - Math.random() * 0.7
        : -Math.PI / 2 + (Math.random() - 0.5) * 1.6;

    bits.push({
      x: fromLeft ? 0 : fromRight ? w : w / 2,
      y: fromLeft || fromRight ? c.clientHeight * 0.9 : c.clientHeight * originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      w: 5 + Math.random() * 7,
      h: 9 + Math.random() * 10,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.4,
      colour: colours[i % colours.length],
      life: 90 + Math.random() * 90,
    });
  }

  if (!raf) raf = requestAnimationFrame(loop);
}

export function stopConfetti(): void {
  bits = [];
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  const c = surface();
  c?.getContext('2d')?.clearRect(0, 0, c.clientWidth, c.clientHeight);
}

/** A ripple of rings from a point, drawn on the bench with plain elements. */
export function ripple(parent: HTMLElement, x: number, y: number, colour: string): void {
  if (reducedMotion()) return;
  const node = h('span', {
    style: { left: `${x}px`, top: `${y}px`, background: colour },
    class: 'ripple',
  });
  parent.appendChild(node);
  setTimeout(() => node.remove(), 700);
}

export function resetChrome(): void {
  clear(host());
  closeSheet();
  stopConfetti();
}

